import { useEffect, useRef, useCallback } from "react";
import { useACLContext } from "../context";
import type { PermissionAction, ResourceType, User } from "@shield-acl/core";

/**
 * Callback para mudança de permissão
 */
export type PermissionChangeCallback<TContext = any> = (
  newValue: boolean,
  previousValue: boolean,
  context: {
    action: PermissionAction;
    resource?: ResourceType;
    user: User<TContext> | null;
  },
) => void;

/**
 * Hook que monitora mudanças em uma permissão específica
 * Útil para reagir quando permissões mudam (ex: após login/logout ou mudança de roles)
 *
 * @param action - Ação a monitorar
 * @param resource - Recurso opcional
 * @param callback - Função chamada quando a permissão muda
 *
 * @example
 * ```tsx
 * // Monitorar mudança em permissão específica
 * usePermissionChange("posts.create", undefined, (canCreate, couldCreate) => {
 *   if (canCreate && !couldCreate) {
 *     toast.success("Agora você pode criar posts!")
 *   } else if (!canCreate && couldCreate) {
 *     toast.warning("Você não pode mais criar posts")
 *   }
 * })
 *
 * // Com recurso específico
 * usePermissionChange("users.edit", "users", (canEdit, couldEdit, context) => {
 *   console.log(`Permissão mudou de ${couldEdit} para ${canEdit}`)
 *   console.log(`Usuário atual: ${context.user?.id}`)
 * })
 *
 * // Em um componente que precisa reagir a mudanças
 * function FeatureGate({ children }) {
 *   const [isEnabled, setIsEnabled] = useState(false)
 *   const can = useCan("feature.access")
 *
 *   usePermissionChange("feature.access", undefined, (newValue) => {
 *     setIsEnabled(newValue)
 *     if (newValue) {
 *       loadFeatureData()
 *     } else {
 *       clearFeatureData()
 *     }
 *   })
 *
 *   return isEnabled ? children : null
 * }
 * ```
 */
export function usePermissionChange<TContext = any>(
  action: PermissionAction,
  resource: ResourceType | undefined,
  callback: PermissionChangeCallback<TContext>,
): void {
  const { can, user } = useACLContext<TContext>();
  const previousValueRef = useRef<boolean | null>(null);
  const previousUserRef = useRef<User<TContext> | null>(user);

  useEffect(() => {
    // Calcula o valor atual da permissão
    const currentValue = can(action, resource);

    // Na primeira execução, apenas armazena o valor
    if (previousValueRef.current === null) {
      previousValueRef.current = currentValue;
      previousUserRef.current = user;
      return;
    }

    // Verifica se houve mudança na permissão ou no usuário
    const hasPermissionChanged = currentValue !== previousValueRef.current;
    const hasUserChanged = user?.id !== previousUserRef.current?.id;

    if (hasPermissionChanged || hasUserChanged) {
      // Chama o callback com os valores
      callback(currentValue, previousValueRef.current, {
        action,
        resource,
        user,
      });

      // Atualiza os valores anteriores
      previousValueRef.current = currentValue;
      previousUserRef.current = user;
    }
  }, [can, user, action, resource, callback]);
}

/**
 * Hook que monitora mudanças em múltiplas permissões
 *
 * @param permissions - Array de permissões para monitorar
 * @param callback - Função chamada quando qualquer permissão muda
 *
 * @example
 * ```tsx
 * // Monitorar múltiplas permissões
 * useMultiplePermissionChange(
 *   [
 *     { action: "posts.create" },
 *     { action: "posts.edit", resource: "posts" },
 *     { action: "posts.delete", resource: "posts" }
 *   ],
 *   (changes) => {
 *     changes.forEach(({ action, resource, newValue, previousValue }) => {
 *       if (newValue !== previousValue) {
 *         console.log(`${action} mudou de ${previousValue} para ${newValue}`)
 *       }
 *     })
 *   }
 * )
 * ```
 */
export function useMultiplePermissionChange<TContext = any>(
  permissions: Array<{ action: PermissionAction; resource?: ResourceType }>,
  callback: (
    changes: Array<{
      action: PermissionAction;
      resource?: ResourceType;
      newValue: boolean;
      previousValue: boolean;
    }>,
  ) => void,
): void {
  const { can, user } = useACLContext<TContext>();
  const previousValuesRef = useRef<Map<string, boolean>>(new Map());
  const previousUserRef = useRef<User<TContext> | null>(user);

  useEffect(() => {
    const changes: Array<{
      action: PermissionAction;
      resource?: ResourceType;
      newValue: boolean;
      previousValue: boolean;
    }> = [];

    let hasAnyChange = false;
    const hasUserChanged = user?.id !== previousUserRef.current?.id;

    // Verifica cada permissão
    permissions.forEach(({ action, resource }) => {
      const key = `${action}:${resource || "none"}`;
      const currentValue = can(action, resource);
      const previousValue = previousValuesRef.current.get(key);

      // Na primeira execução ou mudança de usuário, armazena todos os valores
      if (previousValue === undefined || hasUserChanged) {
        previousValuesRef.current.set(key, currentValue);
        if (previousValue !== undefined) {
          hasAnyChange = true;
          changes.push({
            action,
            resource,
            newValue: currentValue,
            previousValue,
          });
        }
      } else if (currentValue !== previousValue) {
        // Houve mudança na permissão
        hasAnyChange = true;
        previousValuesRef.current.set(key, currentValue);
        changes.push({
          action,
          resource,
          newValue: currentValue,
          previousValue,
        });
      }
    });

    // Se houve alguma mudança, chama o callback
    if (hasAnyChange) {
      callback(changes);
    }

    // Atualiza referência do usuário
    previousUserRef.current = user;
  }, [can, user, permissions, callback]);
}

/**
 * Hook que executa um efeito quando o usuário ganha ou perde uma permissão
 *
 * @param action - Ação a monitorar
 * @param resource - Recurso opcional
 * @param onGain - Callback quando ganha a permissão
 * @param onLose - Callback quando perde a permissão
 *
 * @example
 * ```tsx
 * // Executar ações quando ganha/perde permissão
 * usePermissionEffect(
 *   "admin.access",
 *   undefined,
 *   () => {
 *     // Ganhou acesso admin
 *     loadAdminDashboard()
 *     showAdminMenu()
 *   },
 *   () => {
 *     // Perdeu acesso admin
 *     redirectToHome()
 *     hideAdminMenu()
 *   }
 * )
 * ```
 */
export function usePermissionEffect<TContext = any>(
  action: PermissionAction,
  resource: ResourceType | undefined,
  onGain?: () => void,
  onLose?: () => void,
): void {
  usePermissionChange<TContext>(action, resource, (newValue, previousValue) => {
    if (newValue && !previousValue && onGain) {
      onGain();
    } else if (!newValue && previousValue && onLose) {
      onLose();
    }
  });
}

/**
 * Hook que retorna uma função para verificar se uma permissão mudou
 * Útil para verificações manuais ou condicionais
 *
 * @returns Função para registrar e verificar mudanças
 *
 * @example
 * ```tsx
 * const checkPermissionChange = usePermissionChangeDetector()
 *
 * // Registrar estado inicial
 * const tracker = checkPermissionChange("posts.create")
 *
 * // Mais tarde, verificar se mudou
 * setTimeout(() => {
 *   if (tracker.hasChanged()) {
 *     console.log(`Mudou de ${tracker.previousValue} para ${tracker.currentValue}`)
 *   }
 * }, 5000)
 * ```
 */
export function usePermissionChangeDetector<TContext = any>() {
  const { can } = useACLContext<TContext>();

  return useCallback(
    (action: PermissionAction, resource?: ResourceType) => {
      const initialValue = can(action, resource);

      return {
        action,
        resource,
        previousValue: initialValue,
        get currentValue() {
          return can(action, resource);
        },
        hasChanged() {
          return this.currentValue !== this.previousValue;
        },
      };
    },
    [can],
  );
}
